import { getFormRoles } from '@/lib/api/formRoleApi'
import { FormRole } from '@/typings/domain'
import { createAsyncThunk, createEntityAdapter, createSelector, createSlice } from '@reduxjs/toolkit'
import { RootState } from '../store'
import { QueryState } from './queryStateType'

export type ShallowFormRole = FormRole & {
  formToken: string
}

type StateType = {
  queryState: {
    [formToken: string]: QueryState
  }
}

const initialState: StateType = { queryState: {} }

const formRoleAdapter = createEntityAdapter<ShallowFormRole, string>({
  selectId: model => `${model.formToken}:${model.id}`,
})

const formRoleSlice = createSlice({
  name: 'formRoles',
  initialState: formRoleAdapter.getInitialState(initialState),
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchFormRolesThunk.pending, (state, action) => {
        const formToken = action.meta.arg.formToken
        if (state.queryState[formToken]) {
          state.queryState[formToken].isLoading = true
        } else {
          state.queryState[formToken] = {
            isLoading: false,
            loaded: false,
            error: null,
          }
        }
      })
      .addCase(fetchFormRolesThunk.fulfilled, (state, action) => {
        const formToken = action.meta.arg.formToken
        if (state.queryState[formToken]) {
          state.queryState[formToken].loaded = true
        }
        const nodes = action.payload.map(node => ({
          ...node,
          formToken,
        }))
        formRoleAdapter.upsertMany(state, nodes as ShallowFormRole[])
      })
      .addCase(fetchFormRolesThunk.rejected, (state, action) => {
        const formToken = action.meta.arg.formToken
        if (state.queryState[formToken]) {
          state.queryState[formToken].isLoading = false
          state.queryState[formToken].error = action.error.message
        }
      })
  },
})

type FetchFormRolesArgs = {
  formToken: string
  after?: string
  first: number
}

export const fetchFormRolesThunk = createAsyncThunk<FormRole[], FetchFormRolesArgs>(
  'formRoles/fetchFormRoles',
  async (payload, thunkAPI) => {
    const { data } = await getFormRoles({
      formToken: payload.formToken,
      after: payload.after,
      first: payload.first,
    })

    return data?.form?.formRoles?.nodes
      ? (data.form.formRoles.nodes as FormRole[])
      : thunkAPI.rejectWithValue('failed to fetch form roles')
  }
)

export const formRoleSelector = formRoleAdapter.getSelectors()

export const selectFormRolesQueryStateByFormToken = createSelector(
  (state: RootState) => state.formRoles.queryState,
  (_state: RootState, formToken: string) => formToken,
  (state, formToken) => state[formToken]
)

export const selectFormRolesByFormToken = createSelector(
  (state: RootState) => state.formRoles,
  (_state: RootState, formToken: string) => formToken,
  (state, formToken) => {
    const roleOrder: Record<string, number> = {
      manager: 1,
      data_maintainer: 2,
      data_viewer: 3,
    }

    return Object.values(state.entities)
      .filter(role => role.formToken === formToken)
      .sort((a, b) => {
        const aOrder = a.originalName ? roleOrder[a.originalName] || 999 : 1000
        const bOrder = b.originalName ? roleOrder[b.originalName] || 999 : 1000
        return aOrder - bOrder
      })
  }
)

export default formRoleSlice.reducer
